import React from 'react';
import { Link } from 'react-router-dom';
import { MarketingHeader } from '@/components/layout/MarketingHeader';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { pageTranslations } from '@/i18n/pageTranslations';

const MR = { fontFamily: 'Manrope, sans-serif' };
const CONTACT = 'info@yourbrandvalue.com';

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h2 className="text-lg font-semibold text-foreground mb-3" style={MR}>{title}</h2>
      <div className="text-sm text-muted-foreground leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

function LegalSection({ s }) {
  return (
    <Section title={s.title}>
      {s.content.map((p, i) => <p key={i}>{p}</p>)}
      {s.list && (
        <ul className="list-disc pl-5 space-y-1.5">
          {s.list.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      )}
      {s.richList && (
        <ul className="list-disc pl-5 space-y-1.5">
          {s.richList.map((item, i) => (
            <li key={i}><strong className="text-foreground/80">{item.bold}</strong> {item.text}</li>
          ))}
        </ul>
      )}
      {s.after && <p>{s.after}</p>}
      {s.showContact && (
        <p><a href={`mailto:${CONTACT}`} className="text-primary hover:underline">{CONTACT}</a></p>
      )}
    </Section>
  );
}

export default function TermsPage() {
  const { lang } = useLanguage();
  const t = (pageTranslations[lang] || pageTranslations.en).terms;

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <MarketingHeader />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 md:px-8 pt-28 pb-20 sm:pt-32 sm:pb-24">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors" data-testid="terms-back-link">
          <ArrowLeft className="w-4 h-4" /> {t.backLink}
        </Link>

        <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2" style={MR} data-testid="terms-heading">
          {t.heading}
        </h1>
        <p className="text-sm text-muted-foreground mb-10">
          {t.updated}
        </p>

        <div data-testid="terms-content">
          {t.preamble && (
            <div className="mb-10 rounded-xl border border-primary/15 bg-primary/[0.03] p-6" data-testid="terms-preamble">
              <h2 className="text-lg font-semibold text-foreground mb-3" style={MR}>{t.preamble.title}</h2>
              <div className="text-sm text-muted-foreground leading-relaxed space-y-2">
                {t.preamble.content.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
          )}
          {t.sections.map((s, i) => <LegalSection key={i} s={s} />)}
        </div>
      </main>

      <footer className="border-t border-border/40 py-8">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <p className="text-[11px] text-muted-foreground/30">
            &copy; {new Date().getFullYear()} {t.footer}
          </p>
        </div>
      </footer>
    </div>
  );
}
